// ============================================================================
// OBT F2-B — Filtre dışı park önerisi diyaloğu
// ============================================================================
// Aday parklar YALNIZ aktörün kapsamındaki güvenli `/parks` ucundan gelir
// (sunucu daraltır; tüm belediye parkları istemcide filtrelenmez). Kampanya
// kapsamındaki parklar (my-parks) hariç tutulur. Açıklama + ≥1 fotoğraf zorunlu.
// ============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { listMyResponsibleParks, createSuggestedPark } from './responseApi';
import { normalizeResponseError } from './responseErrors';
import { validateSuggestion, validatePhotoFile } from './responseValidation';
import { suggestedParkStatusLabel } from './responseEnums';

/**
 * @param {object} props
 * @param {string} props.publicationId
 * @param {Set<string>} props.scopedParkIds  kampanya kapsamındaki park_id'ler (hariç tutulur)
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {() => void} [props.onSuccess]
 */
export default function SuggestedParkDialog({
  publicationId,
  scopedParkIds,
  open,
  onOpenChange,
  onSuccess,
}) {
  const [parks, setParks] = useState([]);
  const [parksLoading, setParksLoading] = useState(false);
  const [parksError, setParksError] = useState(null);
  const [parkId, setParkId] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  // Diyalog açıldığında aday parkları yükle + state sıfırla.
  useEffect(() => {
    if (!open) return;
    setParkId('');
    setDescription('');
    setFiles([]);
    setErrors([]);
    setResult(null);
    setParksLoading(true);
    setParksError(null);
    const controller = new AbortController();
    listMyResponsibleParks({ signal: controller.signal })
      .then((rows) => setParks(rows))
      .catch((e) => {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        setParksError(normalizeResponseError(e));
      })
      .finally(() => setParksLoading(false));
    return () => controller.abort();
  }, [open]);

  // Kampanya kapsamında OLMAYAN parklar (öneriye uygun adaylar).
  const candidateParks = useMemo(
    () => parks.filter((p) => !scopedParkIds?.has(String(p.id))),
    [parks, scopedParkIds]
  );

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = '';
    const valid = [];
    for (const f of picked) {
      const check = validatePhotoFile(f);
      if (!check.ok) {
        toast.error(`${f.name}: ${check.message}`);
        continue;
      }
      valid.push(f);
    }
    setFiles((prev) => [...prev, ...valid]);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    const check = validateSuggestion({ parkId, description, files });
    if (!check.ok) {
      setErrors(check.errors);
      return;
    }
    setErrors([]);
    setSubmitting(true);
    try {
      const out = await createSuggestedPark(publicationId, {
        parkId,
        description: description.trim(),
        files,
      });
      setResult(out);
      toast.success('Park önerisi gönderildi.');
      onSuccess?.();
    } catch (e) {
      const n = normalizeResponseError(e);
      setErrors([n.message]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Filtre dışı park öner</DialogTitle>
          <DialogDescription>
            Bilgi talebi filtresine girmeyen ancak ilgili özelliği sahada bulunan bir
            parkı önerin. Açıklama ve en az bir fotoğraf zorunludur.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <CheckCircle2 className="h-8 w-8 text-green-600" aria-hidden="true" />
            <p className="text-sm font-semibold text-gray-800">Öneri gönderildi</p>
            <p className="text-xs text-gray-500">
              Durum: {suggestedParkStatusLabel(result.status)}
            </p>
            <p className="max-w-sm text-xs text-gray-500">
              Öneriniz Bölge Mühendisi incelemesine iletildi. Onaylanırsa park,
              yenileme sonrası cevap listenizde görünecektir.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="sp-park">Park *</Label>
              {parksLoading ? (
                <p className="text-xs text-gray-500">Parklar yükleniyor…</p>
              ) : parksError ? (
                <p className="text-xs text-red-700">{parksError.message}</p>
              ) : candidateParks.length === 0 ? (
                <p className="text-xs text-gray-500">
                  Önerilebilecek (kapsam dışı) park bulunmuyor.
                </p>
              ) : (
                <Select value={parkId} onValueChange={setParkId}>
                  <SelectTrigger id="sp-park">
                    <SelectValue placeholder="Park seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {candidateParks.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sp-desc">Açıklama *</Label>
              <Textarea
                id="sp-desc"
                value={description}
                maxLength={2000}
                placeholder="Bu parkta ilgili özelliğin bulunduğunu açıklayın"
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sp-files">Fotoğraflar * (JPEG/PNG/WebP)</Label>
              <input
                ref={fileRef}
                id="sp-files"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                aria-label="Öneri fotoğrafları ekle"
                className="block text-sm"
                onChange={onPickFiles}
              />
              {files.length > 0 && (
                <ul className="space-y-1 text-xs text-gray-600">
                  {files.map((f, i) => (
                    <li key={i} className="flex items-center justify-between gap-2">
                      <span className="truncate">{f.name}</span>
                      <button
                        type="button"
                        className="text-red-600 hover:underline"
                        onClick={() => removeFile(i)}
                      >
                        Kaldır
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {errors.length > 0 && (
              <div role="alert" className="rounded-md bg-red-50 p-2 text-xs text-red-800">
                <ul className="list-inside list-disc space-y-0.5">
                  {errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => onOpenChange(false)}>Kapat</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Vazgeç
              </Button>
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                Öneriyi Gönder
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
